import { INVOICE_CALCULATION_VERSION } from './enums.js';
import type {
  CreateInvoiceDraftBody,
  InvoiceDraftLineInput,
  InvoiceLineItem,
  InvoiceTotals,
} from './invoice-schemas.js';
import { assertMinorUnits, MoneyParseError } from './money.js';

export class InvoiceCalculationError extends MoneyParseError {
  constructor(message: string) {
    super(message);
    this.name = 'InvoiceCalculationError';
  }
}

export interface CalculatedInvoiceDraft {
  lineItems: InvoiceLineItem[];
  totals: InvoiceTotals;
  taxConfirmed: boolean;
}

export function calculateManualInvoice(
  input: Pick<
    CreateInvoiceDraftBody,
    'lineItems' | 'discountMinor' | 'shippingMinor' | 'taxMinor' | 'taxConfirmed' | 'currency'
  >,
): CalculatedInvoiceDraft {
  const lineItems = input.lineItems.map(calculateLine);
  const subtotalMinor = lineItems.reduce((sum, line) => sum + line.lineNetMinor, 0);
  const discountMinor = assertMinorUnits(input.discountMinor ?? 0, 'order discount');
  const shippingMinor = assertMinorUnits(input.shippingMinor ?? 0, 'shipping');
  const taxMinor = assertMinorUnits(input.taxMinor ?? 0, 'tax');
  const taxConfirmed = input.taxConfirmed === true;

  if (discountMinor > subtotalMinor) {
    throw new InvoiceCalculationError('The order discount cannot exceed the line-item subtotal.');
  }
  if (taxMinor > 0 && !taxConfirmed) {
    throw new InvoiceCalculationError(
      'Manually entered tax must be confirmed before the draft can be saved.',
    );
  }

  const totalMinor = subtotalMinor - discountMinor + shippingMinor + taxMinor;
  if (totalMinor < 0) {
    throw new InvoiceCalculationError('Invoice total cannot be negative.');
  }

  return {
    lineItems,
    taxConfirmed,
    totals: {
      subtotalMinor,
      discountMinor,
      shippingMinor,
      taxMinor,
      totalMinor,
      currency: input.currency ?? 'USD',
      calculationVersion: INVOICE_CALCULATION_VERSION,
    },
  };
}

function calculateLine(line: InvoiceDraftLineInput): InvoiceLineItem {
  const unitPriceMinor = assertMinorUnits(line.unitPriceMinor, 'unit price');
  const discountMinor = assertMinorUnits(line.discountMinor ?? 0, 'line discount');
  const lineGross = unitPriceMinor * line.quantity;
  if (discountMinor > lineGross) {
    throw new InvoiceCalculationError('A line discount cannot exceed the line amount.');
  }
  return {
    ...line,
    discountMinor,
    lineNetMinor: lineGross - discountMinor,
  };
}
